import { useMemo, useState, useCallback } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  useReactFlow,
  useNodesState,
  useEdgesState,
  EdgeMouseHandler,
  NodeChange,
} from '@xyflow/react';

import classNames from 'classnames';

import { IconButton } from '@/components/button';
import { Icon } from '@/components/icon';
import { Tooltip } from '@/components/tooltip';
import { Modal } from '@/components/modal';

import type { ModelExplorerProps } from './types';
import { calculateLayout } from './layout';
import ModelNode from './ModelNode';
import { ModelEdge, ModelEdgeData } from './ModelEdge';

import styles from './models.module.scss';
import '@xyflow/react/dist/style.css';

const nodeTypes = { modelNode: ModelNode };
const edgeTypes = { modelEdge: ModelEdge };

const ModelExplorerInner = ({ models }: ModelExplorerProps) => {
  const { fitView, zoomIn, zoomOut } = useReactFlow();
  const [isFullScreen, setIsFullScreen] = useState(false);

  const graphData = useMemo(() => {
    return calculateLayout(models);
  }, [models]);

  const [nodes, setNodes, onNodesChange] = useNodesState(graphData.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(graphData.edges);

  const [isDragging, setIsDragging] = useState(false);

  const [highlightedRelations, setHighlightedRelations] = useState<
    Array<{
      edgeId: string;
      sourcePropertyKey: string;
      targetPropertyKey: string;
    }>
  >([]);

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      const isDraggingNow = changes.some(
        (change) => change.type === 'position' && change.dragging === true,
      );

      if (isDraggingNow !== isDragging) {
        setIsDragging(isDraggingNow);

        if (isDraggingNow) {
          setHighlightedRelations([]);
          setSelectedNodeId(null);
        }
      }

      onNodesChange(changes as any);
    },
    [isDragging, onNodesChange],
  );

  useMemo(() => {
    setNodes(graphData.nodes);
    setEdges(graphData.edges);
  }, [graphData, setNodes, setEdges]);

  useMemo(() => {
    setNodes((currentNodes) =>
      currentNodes.map((node) => {
        let highlightedPropertyKey: string | undefined;

        if (highlightedRelations.length > 0) {
          for (const relation of highlightedRelations) {
            const edge = edges.find((e) => e.id === relation.edgeId);
            if (edge) {
              if (edge.source === node.id) {
                highlightedPropertyKey = relation.sourcePropertyKey;
                break;
              } else if (edge.target === node.id) {
                highlightedPropertyKey = relation.targetPropertyKey;
                break;
              }
            }
          }
        }

        const onPropertyHover = (propertyKey: string | null) => {
          if (isDragging || selectedNodeId !== null) {
            return;
          }

          if (propertyKey === null) {
            setHighlightedRelations([]);
            return;
          }

          const relatedEdges = edges.filter(
            (e) =>
              (e.source === node.id && e.data?.joinKeyOnBase === propertyKey) ||
              (e.target === node.id && e.data?.joinKeyOnRelated === propertyKey),
          );

          const relations = relatedEdges
            .filter(
              (edge) =>
                edge.data?.joinKeyOnBase !== undefined && edge.data?.joinKeyOnRelated !== undefined,
            )
            .map((edge) => ({
              edgeId: edge.id,
              sourcePropertyKey: edge.data!.joinKeyOnBase!,
              targetPropertyKey: edge.data!.joinKeyOnRelated!,
            }));

          setHighlightedRelations(relations);
        };

        return {
          ...node,
          data: {
            ...node.data,
            highlightedPropertyKey,
            onPropertyHover,
          },
        };
      }),
    );
  }, [highlightedRelations, edges, isDragging, selectedNodeId, setNodes]);

  useMemo(() => {
    setEdges((currentEdges) =>
      currentEdges.map((edge) => ({
        ...edge,
        data: {
          ...edge.data,
          isHovered: highlightedRelations.some((relation) => relation.edgeId === edge.id),
        },
      })),
    );
  }, [highlightedRelations, setEdges]);

  const handleEdgeMouseEnter: EdgeMouseHandler = useCallback(
    (_, edge) => {
      if (selectedNodeId !== null) {
        return;
      }

      const edgeData = edge.data as ModelEdgeData | undefined;
      if (edgeData?.joinKeyOnBase !== undefined && edgeData?.joinKeyOnRelated !== undefined) {
        setHighlightedRelations([
          {
            edgeId: edge.id,
            sourcePropertyKey: edgeData.joinKeyOnBase,
            targetPropertyKey: edgeData.joinKeyOnRelated,
          },
        ]);
      }
    },
    [selectedNodeId],
  );

  const handleEdgeMouseLeave: EdgeMouseHandler = useCallback(() => {
    if (selectedNodeId !== null) {
      return;
    }
    setHighlightedRelations([]);
  }, [selectedNodeId]);

  const handleNodeClick = useCallback(
    (_: any, node: any) => {
      setSelectedNodeId(node.id);

      const connectedEdges = edges.filter(
        (edge) => edge.source === node.id || edge.target === node.id,
      );

      const relations = connectedEdges
        .filter(
          (edge) =>
            edge.data?.joinKeyOnBase !== undefined && edge.data?.joinKeyOnRelated !== undefined,
        )
        .map((edge) => ({
          edgeId: edge.id,
          sourcePropertyKey: edge.data!.joinKeyOnBase!,
          targetPropertyKey: edge.data!.joinKeyOnRelated!,
        }));

      setHighlightedRelations(relations);
    },
    [edges],
  );

  const handlePaneClick = useCallback(() => {
    setSelectedNodeId(null);
    setHighlightedRelations([]);
  }, []);

  if (models.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Icon name="Model" size={48} />
        <p>No models found</p>
      </div>
    );
  }

  const flowContent = (
    <div className={classNames(isFullScreen ? styles.fullScreenContainer : styles.graphContainer)}>
      <div className={styles.relative}>
        <div className={styles.controls}>
          <Tooltip content="Zoom out">
            <IconButton
              tabIndex={1}
              type="gray"
              icon="ZoomOut"
              size="small"
              onClick={() => zoomOut()}
            />
          </Tooltip>
          <Tooltip content="Zoom in">
            <IconButton type="gray" icon="ZoomIn" size="small" onClick={() => zoomIn()} />
          </Tooltip>
          <Tooltip content="Fit to view">
            <IconButton
              type="gray"
              icon="Maximize"
              size="small"
              onClick={() => fitView({ padding: 0.2, duration: 300 })}
            />
          </Tooltip>
          {!isFullScreen ? (
            <Tooltip content="Full screen">
              <IconButton
                type="gray"
                icon="Maximize2"
                size="small"
                onClick={() => setIsFullScreen(true)}
              />
            </Tooltip>
          ) : (
            <Tooltip content="Minimize">
              <IconButton
                type="gray"
                icon="Minimize2"
                size="small"
                onClick={() => setIsFullScreen(false)}
              />
            </Tooltip>
          )}
        </div>

        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={handleNodesChange}
          onEdgesChange={onEdgesChange}
          onEdgeMouseEnter={handleEdgeMouseEnter}
          onEdgeMouseLeave={handleEdgeMouseLeave}
          onNodeClick={handleNodeClick}
          onPaneClick={handlePaneClick}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          minZoom={0.1}
          maxZoom={3}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          colorMode="dark"
          panOnScroll
          panOnScrollSpeed={1.2}
          proOptions={{ hideAttribution: true }}>
          <Background variant={BackgroundVariant.Dots} gap={32} size={2} color="#20222e" />
        </ReactFlow>
      </div>
    </div>
  );

  if (isFullScreen) {
    return (
      <Modal onClose={() => setIsFullScreen(false)} closeOnEsc closeOnClickAway>
        {flowContent}
      </Modal>
    );
  }

  return flowContent;
};

export const ModelExplorer = (props: ModelExplorerProps) => {
  return (
    <ReactFlowProvider>
      <ModelExplorerInner {...props} />
    </ReactFlowProvider>
  );
};
